<template>
  <div class="idphoto-tool">
    <!-- 第一行：上传照片并生成 -->
    <el-card class="photo-card" shadow="never">
      <div slot="header" class="card-header">
        <span><i class="el-icon-upload"></i> 上传照片并生成</span>
      </div>

      <div class="gen-layout">
        <!-- 左：上传 -->
        <div class="upload-pane">
          <el-upload
            class="upload-box"
            drag
            action="#"
            accept="image/jpeg,image/png,image/webp"
            :auto-upload="false"
            :show-file-list="false"
            :on-change="onSourceChange"
          >
            <img v-if="sourcePreview" :src="sourcePreview" class="upload-preview" alt="原图预览" />
            <template v-else>
              <i class="el-icon-upload"></i>
              <div class="el-upload__text">拖拽图片到此处，或 <em>点击上传</em></div>
              <div class="el-upload__tip">支持 jpg / png / webp</div>
            </template>
          </el-upload>
        </div>

        <!-- 右：尺寸与生成 -->
        <div class="form-pane">
          <div class="field-label">证件照尺寸</div>
          <div class="dimension-group">
            <el-radio-group v-model="dimensionPreset" size="small">
              <el-radio-button :label="'one'">一寸 295×413</el-radio-button>
              <el-radio-button :label="'two'">二寸 413×579</el-radio-button>
              <el-radio-button :label="'custom'">自定义</el-radio-button>
            </el-radio-group>
            <div v-if="dimensionPreset === 'custom'" class="dimension-custom">
              <el-input-number v-model="customWidth" :min="1" :max="5000" :controls="false" placeholder="宽" />
              <span class="x-mark">×</span>
              <el-input-number v-model="customHeight" :min="1" :max="5000" :controls="false" placeholder="高" />
            </div>
          </div>
          <div class="tip-text form-tip">常用尺寸：一寸 295×413 / 二寸 413×579 / 签证 390×567</div>

          <el-button
            type="primary"
            :loading="generating"
            :disabled="!sourceFile"
            class="gen-btn"
            @click="generate"
          >
            {{ generating ? '正在生成...' : '生成透明证件照' }}
          </el-button>

          <div v-if="transparentPreview" class="transparent-tip">
            <span class="tip-text">透明底证件照已生成，可在下方换底色</span>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 第二行：换底色即时预览 -->
    <el-card class="photo-card" shadow="never">
      <div slot="header" class="card-header">
        <span><i class="el-icon-magic-stick"></i> 换底色即时预览</span>
      </div>

      <div class="gen-layout">
        <!-- 左：换底色操作 -->
        <div class="color-pane">
          <div class="field-label">底色</div>
          <div class="color-group">
            <el-radio-group v-model="colorMode" size="small" @change="updatePreview">
              <el-radio-button :label="'red'">红</el-radio-button>
              <el-radio-button :label="'blue'">蓝</el-radio-button>
              <el-radio-button :label="'white'">白</el-radio-button>
              <el-radio-button :label="'custom'">自定义</el-radio-button>
            </el-radio-group>
            <el-color-picker
              v-if="colorMode === 'custom'"
              v-model="customColor"
              @change="updatePreview"
            />
          </div>

          <el-button
            type="primary"
            :loading="exporting"
            :disabled="!transparentFile"
            class="gen-btn"
            @click="doExport"
          >
            {{ exporting ? '合成中...' : '导出带底色证件照' }}
          </el-button>

          <el-button
            v-if="resultUrl"
            type="primary"
            class="gen-btn"
            @click="download"
          >
            <i class="el-icon-download"></i> 下载证件照
          </el-button>
        </div>

        <!-- 右：即时预览 -->
        <div class="preview-pane">
          <div class="preview-box">
            <img v-if="previewUrl" :src="previewUrl" class="final-preview" alt="带底色预览" />
            <div v-else class="preview-empty">请先在上方生成透明证件照</div>
          </div>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import { idphoto, addBackground, blobToUrl, downloadUrl, revokeUrl } from '@/api/photo'

const DIMENSIONS = {
  one: { width: 295, height: 413 },
  two: { width: 413, height: 579 }
}
const PRESET_COLORS = {
  red: 'F63737',
  blue: '438EDB',
  white: 'FFFFFF',
  custom: '438EDB'
}

export default {
  name: 'IdPhoto',
  data() {
    return {
      sourceFile: null,
      sourcePreview: '',
      transparentStd: '',
      transparentHd: '',
      transparentPreview: '',
      transparentFile: null,
      dimensionPreset: 'one',
      customWidth: 295,
      customHeight: 413,
      colorMode: 'blue',
      customColor: '#438EDB',
      previewUrl: '',
      resultUrl: '',
      resultName: 'idphoto.jpg',
      generating: false,
      exporting: false
    }
  },
  computed: {
    activeColor() {
      return this.colorMode === 'custom' ? this.customColor.substring(1) : PRESET_COLORS[this.colorMode]
    },
    targetDimension() {
      if (this.dimensionPreset === 'custom') {
        return { width: this.customWidth, height: this.customHeight }
      }
      return DIMENSIONS[this.dimensionPreset]
    }
  },
  methods: {
    onSourceChange(file) {
      this.sourceFile = file.raw || file
      this.sourcePreview = URL.createObjectURL(this.sourceFile)
      this.transparentStd = ''
      this.transparentHd = ''
      this.transparentPreview = ''
      this.transparentFile = null
      this.previewUrl = ''
      this.resultUrl = ''
    },
    async generate() {
      if (!this.sourceFile) {
        this.msgError('请先上传照片')
        return
      }
      const { width, height } = this.targetDimension
      this.generating = true
      try {
        //const res = await idphoto(this.sourceFile, { width, height, faceDetectModel: 'mtcnn' })
        const res = await idphoto(this.sourceFile, { width, height })
        if (res && res.code === 200 && res.data) {
          this.transparentStd = res.data.stdBase64
          this.transparentHd = res.data.hdBase64
          this.transparentPreview = 'data:image/png;base64,' + (this.transparentStd || this.transparentHd)
          this.transparentFile = this.toFile(this.transparentHd || this.transparentStd, 'idphoto.png')
          this.updatePreview()
          this.msgSuccess('证件照生成成功，可换底色预览')
        } else {
          this.msgError((res && res.msg) || '生成失败')
        }
      } catch (e) {
        this.handleError(e)
      } finally {
        this.generating = false
      }
    },
    updatePreview() {
      if (!this.transparentStd && !this.transparentHd) {
        this.previewUrl = ''
        return
      }
      const source = this.transparentStd || this.transparentHd
      const { width, height } = this.targetDimension
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width = width || img.width
        canvas.height = height || img.height
        const ctx = canvas.getContext('2d')
        ctx.fillStyle = '#' + this.activeColor
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        if (width && height) {
          ctx.drawImage(img, 0, 0, width, height)
        } else {
          ctx.drawImage(img, 0, 0)
        }
        this.previewUrl = canvas.toDataURL('image/png')
      }
      img.src = 'data:image/png;base64,' + source
    },
    async doExport() {
      if (!this.transparentFile) {
        this.msgError('请先生成透明证件照')
        return
      }
      this.exporting = true
      try {
        const blob = await addBackground(this.transparentFile, this.activeColor)
        this.resultName = 'idphoto_' + this.targetDimension.width + 'x' + this.targetDimension.height + '.jpg'
        this.replaceResult(blob)
        this.msgSuccess('导出成功，点击下载保存证件照')
      } catch (e) {
        this.handleError(e)
      } finally {
        this.exporting = false
      }
    },
    replaceResult(blob) {
      if (this.resultUrl) {
        revokeUrl(this.resultUrl)
      }
      this.resultUrl = blobToUrl(blob)
    },
    download() {
      if (this.resultUrl) {
        downloadUrl(this.resultUrl, this.resultName)
      }
    },
    toFile(base64, name) {
      const byteCharacters = atob(base64)
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      return new File([new Uint8Array(byteNumbers)], name, { type: 'image/png' })
    },
    handleError(e) {
      const status = e && e.response && e.response.status
      if (status === 429) {
        this.msgError('今日使用次数已达上限，请明天再试')
        this.$alert('您今日的生成次数已经用完，今天请不要再尝试生成了，明天再来吧', '今日已用完', {
          type: 'warning',
          confirmButtonText: '知道了'
        })
      } else if (status === 400) {
        this.msgError('参数有误，请检查输入')
      } else {
        this.msgError('操作失败，请稍后重试')
      }
    }
  }
}
</script>

<style scoped>
.photo-card {
  margin-bottom: 20px;
  border-radius: 8px;
}
.card-header {
  font-weight: 600;
}
.gen-layout {
  display: flex;
  gap: 28px;
  align-items: stretch;
}
.upload-pane {
  flex: 0 0 42%;
}
.upload-pane >>> .el-upload,
.upload-pane >>> .el-upload-dragger {
  width: 100%;
}
.form-pane {
  flex: 1;
  max-width: 560px;
}
.color-pane {
  flex: 0 0 30%;
  text-align: center;
  margin-top: 10%;
}
.color-pane .field-label {
  font-size: 16px;
  color: #303133;
}
.color-pane .color-group {
  justify-content: center;
}
.color-pane >>> .el-radio-button__inner {
  font-size: 15px;
}
.preview-pane {
  flex: 1;
}
.field-label {
  font-size: 13px;
  font-weight: 600;
  color: #606266;
  margin-bottom: 10px;
}
.dimension-group {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.dimension-custom {
  display: flex;
  align-items: center;
  gap: 8px;
}
.color-group {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}
.form-tip {
  margin: 10px 0 0;
}
.gen-btn {
  width: 90%;
  margin-top: 14px;
  margin-left: 1%;
}
.upload-box {
  text-align: center;
}
.upload-preview {
  max-width: 100%;
  max-height: 260px;
  border-radius: 6px;
}
.transparent-tip {
  margin-top: 14px;
  text-align: center;
}
.transparent-tip .tip-text {
  font-size: 14px;
  font-weight: 600;
  color: #67c23a;
}
.preview-box {
  height: 100%;
  min-height: 380px;
  border: 1px dashed #ebeef5;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafafa;
  padding: 12px;
}
.final-preview {
  max-width: 100%;
  max-height: 360px;
  border-radius: 6px;
}
.preview-empty {
  color: #bbb;
  font-size: 14px;
}
.x-mark {
  color: #999;
}
.tip-text {
  font-size: 12px;
  color: #909399;
}
</style>